<template>
  <div id="app" ref="app" :class="{ 'embed-mode': isEmbed }" :data-bp="bp">
    <!--
    <header v-if="!isEmbed">
      <h1>Super Facilitator</h1>
      <nav>
        <router-link to="/" custom v-slot="{ navigate }">
          <button @click="navigate">Sessions</button>
        </router-link>
      </nav>
    </header>
    -->
    <router-view></router-view>
    <!-- Task A11: build-version badge, embed mode only -->
    <div v-if="isEmbed" class="embed-version-badge" :title="'Sparkconverse build ' + buildVersion">
      v{{ buildVersion }}
    </div>
  </div>
</template>

<script>
import { isEmbedMode } from '@/lib/embedMode'
import { BUILD_VERSION } from '@/buildVersion'

// Width breakpoints, measured on the app container itself (not the window):
// inside the Sparkco iFrame (~750px) and full-page alike. styles/theme.css
// and components key their narrow rules off [data-bp].
//   xs  <= 480   compact toolbar (icon-only), bottom-sheet menus/modals
//   md  481-999  the default design (Sparkco embeds us at ~750px)
//   lg  >= 1000  full page, comfortable max-width
const BP_XS_MAX_IN_PX = 480
const BP_LG_MIN_IN_PX = 1000

function breakpointForWidth(width) {
  if (width <= BP_XS_MAX_IN_PX) return 'xs'
  if (width >= BP_LG_MIN_IN_PX) return 'lg'
  return 'md'
}

export default {
  name: 'App',
  data() {
    return {
      bp: 'md'
    }
  },
  mounted() {
    const el = this.$refs.app
    const measure = (width) => {
      this.bp = breakpointForWidth(width)
      document.documentElement.setAttribute('data-bp', this.bp)
    }
    measure(el ? el.getBoundingClientRect().width : window.innerWidth)
    if (typeof ResizeObserver !== 'undefined' && el) {
      this._ro = new ResizeObserver(entries => {
        const entry = entries[0]
        // border-box width: our own padding changes per bp must not feed back
        if (entry) measure(entry.target.getBoundingClientRect().width)
      })
      this._ro.observe(el)
    } else {
      this._onResize = () => measure(window.innerWidth)
      window.addEventListener('resize', this._onResize)
    }
  },
  beforeDestroy() {
    if (this._ro) this._ro.disconnect()
    if (this._onResize) window.removeEventListener('resize', this._onResize)
  },
  computed: {
    // Task A6: lightweight embed layout flag — components use this (or
    // isEmbedMode() directly) to hide org switcher / nav chrome.
    isEmbed() {
      return isEmbedMode()
    },
    buildVersion() {
      return BUILD_VERSION
    }
  }
}
</script>

<style>
#app {
  font-family: var(--sc-font);
  font-size: 14px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  min-height: 100vh;
  background-color: var(--color-background);
  color: var(--color-text-primary);
}

/* md = the ~750px Sparkco iFrame: tighter gutters */
#app[data-bp='md'] {
  padding-left: 12px;
  padding-right: 12px;
}

#app[data-bp='xs'] {
  padding-left: 8px;
  padding-right: 8px;
}

header {
  margin-bottom: 2rem;
  border-bottom: 1px solid var(--color-border);
  padding-bottom: 1rem;
}

.back-button {
  margin-bottom: 1rem;
  height: var(--sc-ctrl-h);
  padding: 0 14px;
  background: transparent;
  color: var(--sc-text-main);
  border: 1px solid var(--sc-faint);
  border-radius: var(--sc-r);
  cursor: pointer;
}

.back-button:hover {
  background: var(--sc-hover);
}

.session-button {
  display: block;
  width: 100%;
  padding: 1rem;
  margin: 0.5rem 0;
  background: var(--sc-surface);
  color: var(--sc-text);
  border: 1px solid var(--sc-line);
  border-radius: var(--sc-r-card);
  cursor: pointer;
  text-align: left;
}

.session-button:hover {
  background: var(--sc-hover);
}

.empty-state {
  text-align: center;
  padding: 2rem;
  color: var(--sc-muted);
  background: var(--sc-row);
  border-radius: var(--sc-r-card);
  margin: 1rem 0;
}

/* Task A6/A11: embed chrome — tighter padding + tiny version badge */
#app.embed-mode {
  padding-left: 8px;
  padding-right: 8px;
}

.embed-version-badge {
  position: fixed;
  bottom: 4px;
  right: 8px;
  font-size: 10px;
  color: var(--sc-faint);
  pointer-events: none;
  z-index: 100;
  font-variant-numeric: tabular-nums;
}
</style>
